import { Meal } from "@/clients/models/Meal";
import { cn } from "@/utils/cn";
import { memo } from "react";

interface MealPreferenceItemProps {
  meal: Meal;
  variant: "like" | "dislike";
  isRemoving: boolean;
  onRemove: (meal: Meal) => void;
}

export const MealPreferenceItem = memo(
  ({ meal, variant, isRemoving, onRemove }: MealPreferenceItemProps) => {
    const removeButtonLabel =
      variant === "like"
        ? "Remove from favourites"
        : "Remove from disliked meals";

    return (
      <article
        className={cn(
          "flex items-center gap-3 rounded-3xl border border-white/10 bg-white px-3 py-2",
          "shadow-[0_4px_14px_rgba(15,23,42,0.05)] min-h-[72px]",
        )}
      >
        <div className="w-16 h-16 rounded-2xl overflow-hidden bg-gray-100 flex-shrink-0">
          {meal.imageUri ? (
            <img
              src={meal.imageUri}
              alt={meal.name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full bg-gray-200" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-base! font-semibold! text-gray-900! line-clamp-2!">
            {meal.name}
          </h4>
        </div>
        <button
          type="button"
          aria-label={removeButtonLabel}
          className={cn(
            "px-4 py-1.5 rounded-full text-xs font-semibold border flex-shrink-0",
            "transition-colors disabled:opacity-60 disabled:cursor-not-allowed",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white",
            variant === "like"
              ? "text-pink-600 border-pink-100 bg-pink-50/30 hover:bg-pink-50 focus-visible:ring-pink-200"
              : "text-red-600 border-red-100 bg-red-50/30 hover:bg-red-50 focus-visible:ring-red-200",
          )}
          disabled={isRemoving}
          onClick={() => onRemove(meal)}
        >
          Remove
        </button>
      </article>
    );
  },
);

MealPreferenceItem.displayName = "MealPreferenceItem";
